import * as Sentry from '@sentry/react';
import type { AxiosError } from 'axios';

Sentry.init({
  dsn: 'https://0b5dbd45d7fbdf40eda0dda3a99077b9@o1181643.ingest.us.sentry.io/4511631400763392',
  environment: import.meta.env.MODE,
  integrations: [Sentry.browserTracingIntegration(), Sentry.replayIntegration()],
  // Performance Monitoring
  tracesSampleRate: 1.0, //  Capture 100% of the transactions
  // Set 'tracePropagationTargets' to control for which URLs distributed tracing should be enabled
  tracePropagationTargets: ['localhost'],
  // Session Replay
  replaysSessionSampleRate: 0.1, // This sets the sample rate at 10%. You may want to change it to 100% while in development and then sample at a lower rate in production.
  replaysOnErrorSampleRate: 1.0, // If you're not already sampling the entire session, change the sample rate to 100% when sampling sessions where errors occur.
  beforeSend(event, hint) {
    const error = hint?.originalException as AxiosError | undefined;

    if (error?.isAxiosError) {
      const status = error.response?.status;

      // Ignore 401 and 404
      if (status === 401 || status === 404) {
        return null; // prevents sending to Sentry
      }
    }

    return event;
  },
});

// ---- AUTH_DEBUG helpers --------------------------------------------------
// Token fingerprint — short prefix + length so tokens are correlatable but never logged raw.
export const fingerprint = (t?: unknown) => (t ? `${String(t).slice(0, 6)}…(${String(t).length})` : 'null');

// Last known user id, kept in module scope so every trail/log can carry it even from call
// sites (use-axios, post-to-app, AuthProvider) that don't have the React user object.
let currentUid: string | number | null | undefined = null;

// Breadcrumb: cheap context, sends nothing on its own — it rides along with the next
// captured event/message. Use for the step-by-step auth trail.
export const authTrail = (message: string, data?: Record<string, unknown>) =>
  Sentry.addBreadcrumb({ category: 'auth', level: 'info', message, data: { uid: currentUid, ...data } });

// Standalone, searchable entry at `warning` level (not an error). Survives the 401/404
// beforeSend filter, so use it at decision points (token clears, 401s, recovery outcome).
export const authLog = (message: string, extra?: Record<string, unknown>) =>
  Sentry.captureMessage(message, { level: 'warning', tags: { area: 'auth' }, extra: { uid: currentUid, ...extra } });

// Attribute all subsequent events/breadcrumbs to a user (id + email is PII — only what we need).
// Pass a falsy value to clear it on logout/auth clear.
// Minimal shape of the app user this module needs (full user type lives with UserProvider).
type SentryUserLike = { id?: string | number; email?: string } | null | undefined;

export const setSentryUser = (user: SentryUserLike) => {
  currentUid = user ? user.id : null;
  Sentry.setUser(user ? { id: user.id, email: user.email } : null);
};
